// src/pages/Orders/GroomingPetDetailsDrawer.jsx
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { 
  FaTimes, FaUser, FaPhone, FaWeight, FaVenusMars, 
  FaClock, FaTag, FaRupeeSign, FaCut, 
  FaSyringe, FaNotesMedical, FaHistory, FaCalendar
} from "react-icons/fa";
import { format, differenceInDays, parseISO, isValid, isPast, isToday } from "date-fns";
import { useColor } from "../../hooks/color/useColor";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { getPetIcon } from "../../components/common/PetDetailsBadge";
import { httpService } from "../../services/httpService";
import PetHeader from "./PetHeader";
import GroomingSummaryPreview from "../../components/common/GroomingSummaryPreview";
const GroomingPetDetailsDrawer = ({
  isOpen,
  onClose,
  petData,
  clientData,
  onRefresh,
  showCompleteTimeline = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("grooming");
  const [petDetails, setPetDetails] = useState(petData || {});
  const [clientDetails, setClientDetails] = useState(clientData || {});
  const [groomingOrders, setGroomingOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [vaccinations, setVaccinations] = useState([]);
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [lastGroomedData, setLastGroomedData] = useState(null);
  const { getColor } = useColor();

  // Format pet details
  const petName = petDetails?.name || "Unknown Pet";
  const petType = petDetails?.pet_type?.name || petDetails?.pet_type_name || "Dog";
  const petBreed = petDetails?.breed?.breed_name || petDetails?.breed_name || "Unknown Breed";
  const petAge = petDetails?.age || "Unknown";
  const petGender = petDetails?.gender || "Unknown";
  const petWeight = petDetails?.weight_kg || petDetails?.weight || "N/A";
  const petImage = petDetails?.image || petDetails?.pet_images?.[0] || null;
  const petCoat = petDetails?.hair_type || petDetails?.breed?.breed_coat || petDetails?.coat || "—";
  const petTags = petDetails?.tags || petDetails?.pet_tags || [];
  const petColor = petDetails?.color || petDetails?.colors || petDetails?.pet_color || [];
  const petDiet = petDetails?.meal_type || petDetails?.mealType || petDetails?.diet || petDetails?.what_to_feed || [];
  const petMealsPerDay = petDetails?.meals_per_day || petDetails?.mealsPerDay || null;
  const petSize = petDetails?.size?.size_label || petDetails?.size?.name || petDetails?.size || "";
  const petNeuteredStatus = petDetails?.neutered_status || petDetails?.neutered_spayed || "Unknown";
  const petMedicalConditions = petDetails?.medical_conditions || petDetails?.medical_condition || [];
  const petVaccinations = petDetails?.vaccinations || petDetails?.vaccinated_for || petDetails?.vaccines || [];
  const petId = petDetails?._id || petDetails?.vendor_pet_id;
  const clientId = petDetails?.vendor_client_id || clientData?._id;
   
  const tabs = [
    { id: "grooming", label: "Grooming", icon: FaCut },
    { id: "overview", label: "Overview", icon: FaHistory },
     ...(showCompleteTimeline
    ? [{ id: "overall", label: "Complete Timeline", icon: FaHistory }]
    : []),
  ];
  // Client details
  const clientName = clientDetails?.firstname && clientDetails?.lastname 
    ? `${clientDetails.firstname} ${clientDetails.lastname}` 
    : clientDetails?.name || "Unknown Owner";
  const clientPhone = clientDetails?.phone || clientDetails?.mobile || "N/A";

  // Fetch client data
  const fetchClientData = async () => {
    if (!clientId || !petId) {
      console.warn("Missing clientId or petId");
      return;
    }

    setLoading(true);
    try {
      // Fetch client overview with all orders
      const response = await httpService.get(
        `/vendorclient/getVendorClientPetsOverviewById/${clientId}`
      );
      
      if (response?.code === 200 && response?.data?.data) {
        const clientDataFromApi = response.data.data;
        
        // Update client details
        setClientDetails(prev => ({
          ...prev,
          firstname: clientDataFromApi.firstname || prev?.firstname,
          lastname: clientDataFromApi.lastname || prev?.lastname,
          phone: clientDataFromApi.phone || prev?.phone,
          email: clientDataFromApi.email || prev?.email,
          address: clientDataFromApi.address || prev?.address,
        }));

        // Extract pet details from response if not already set
        let foundPet = null;
        if (clientDataFromApi.pet_details) {
        foundPet = clientDataFromApi.pet_details.find(p => p._id === petId);
        if (foundPet) {
            setPetDetails(prev => ({ ...prev, ...foundPet }));
        }
        }
        // Process orders - Use a Set to track unique order_ids to avoid duplicates
        const processedOrderIds = new Set();
        const allOrdersList = [];
        
        // Helper to process an order
        const processOrder = (order) => {
          if (!order || !order.items || !Array.isArray(order.items)) return;
          
          order.items.forEach(item => {
            // Check if this item belongs to this pet
            const itemPetId = item.vendor_pet_id || item.pet_id || 
              item.appointment_info?.vendor_pet_id || 
              item.boarding_info?.vendor_pet_id;
            
            if (itemPetId === petId) {
              // Create a unique key to avoid duplicates
              const uniqueKey = `${order._id}_${item._id || item.ref_id}`;
              if (processedOrderIds.has(uniqueKey)) return;
              processedOrderIds.add(uniqueKey);
              
              const booking = extractBookingFromItem(item, order);
              if (booking) {
                allOrdersList.push(booking);
              }
            }
          });
        };

        // Process all order sources
        if (clientDataFromApi.order_details) {
          clientDataFromApi.order_details.forEach(processOrder);
        }
        
        if (clientDataFromApi.upcoming_appointment_details) {
          clientDataFromApi.upcoming_appointment_details.forEach(processOrder);
        }
        
        if (clientDataFromApi.completed_cancelled_appointments) {
          clientDataFromApi.completed_cancelled_appointments.forEach(processOrder);
        }

        // Sort by date (newest first)
        allOrdersList.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Separate grooming orders (only type === "grooming")
        const grooming = allOrdersList.filter(b => b.type === "grooming");
        
        // Find last COMPLETED or STARTED grooming (only past services, not future)
        // "Confirmed" appointments are future bookings and should NOT count as "last groomed"
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // End of today (for completed/started services that happened today)
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);

        const eligibleGrooming = grooming.filter(b => {
        const bookingDate = new Date(b.date);
        bookingDate.setHours(0, 0, 0, 0);
        
        // Only include grooming that has started or completed (not future confirmed)
        const status = b.status?.toLowerCase();
        const isPastOrCurrent = status === "completed" || status === "started" || status === "closed";;
        
        // For started/completed, check if date is <= today
        return isPastOrCurrent && bookingDate <= today;
        });
        
        const lastGrooming = eligibleGrooming.length > 0 ? eligibleGrooming[0] : null;
        setLastGroomedData(lastGrooming);

        setAllOrders(allOrdersList);
        setGroomingOrders(grooming);

        // Prefer the pet that came from the API (foundPet), otherwise fall back to current state
        const sourcePet = foundPet || petDetails;

        // Vaccinations
        if (sourcePet?.vaccination_records?.length > 0) {
        setVaccinations(sourcePet.vaccination_records);
        } else if (Array.isArray(sourcePet?.vaccinations) && sourcePet.vaccinations.length > 0) {
        setVaccinations(sourcePet.vaccinations.map(v => ({ vaccine: v })));
        } else {
        setVaccinations([]);
        }

        // Medical conditions
        if (sourcePet?.medical_condition?.length > 0) {
        setMedicalConditions(sourcePet.medical_condition);
        } else {
        setMedicalConditions([]);
        }

        setLoading(false);
      } else {
        console.error("❌ API returned unexpected response:", response);
        setLoading(false);
      }
    } catch (error) {
      console.error("❌ Error fetching client data:", error);
      setLoading(false);
    }
  };

  // Helper to extract booking from item
  const extractBookingFromItem = (item, order) => {
    try {
      const isGrooming = item.type === "grooming";
      const isBoarding = item.type === "boarding" || item.type === "day_care";
      const isVet = item.type === "vet_clinic";
      
      let serviceInfo = {};
      let date = null;
      let endDate = null;
      let employeeName = "N/A";
      let serviceName = "Service";
      let roomName = "N/A";
      let roomNumber = "N/A";
      let type = item.type;
      let status = item.status || order.status || "pending";
      let price = item.final_total || item.total_price || item.price || 0;
      let paidAmount = item.total_paid_amount || item.paid_amount || item.paid || 0;
      let orderNumber = order.order_number || order._id;
      let orderId = order._id;
      let startTime = null;
      let duration = null;

      if (isGrooming && item.appointment_info) {
        serviceInfo = item.appointment_info;
        date = serviceInfo.appointment_date || order.createdAt;
        employeeName = serviceInfo.employee_details?.name || serviceInfo.employee_name || "N/A";
        serviceName = serviceInfo.grooming_services?.name || item.service_name || "Grooming Service";
        status = serviceInfo.status || status;
        type = "grooming";
        startTime = serviceInfo.time_slot || item.start_time || null;
        duration = serviceInfo.duration || item.duration || null;
      } else if (isBoarding && item.boarding_info) {
        serviceInfo = item.boarding_info;
        date = serviceInfo.start_date || order.createdAt;
        endDate = serviceInfo.end_date;
        employeeName = serviceInfo.room_details?.room_name || "N/A";
        roomName = serviceInfo.room_details?.room_name || "N/A";
        roomNumber = serviceInfo.room_details?.room_numbers?.[0]?.room_number || "N/A";
        serviceName = serviceInfo.boarding_services?.name || item.service_name || "Boarding Service";
        status = serviceInfo.status || status;
        type = "boarding";
      } else if (isVet && item.appointment_info) {
        serviceInfo = item.appointment_info;
        date = serviceInfo.appointment_date || order.createdAt;
        employeeName = serviceInfo.employee_details?.name || serviceInfo.employee_name || "N/A";
        serviceName = serviceInfo.vetclinic_services?.name || item.service_name || "Vet Service";
        status = serviceInfo.status || status;
        type = "vet_clinic";
        startTime = serviceInfo.time_slot || item.start_time || null;
        duration = serviceInfo.duration || item.duration || null;
      } else {
        date = order.createdAt || order.start_date;
        serviceName = item.service_name || item.name || "Service";
        price = item.final_total || item.total_price || item.price || 0;
        status = item.status || order.status || "pending";
      }

      if (!date) return null;

      return {
        date: date,
        end_date: endDate,
        service_name: serviceName,
        price: Number(price).toFixed(1),
        paid_amount: Number(paidAmount) || 0,
        employee_name: employeeName,
        status: status,
        order_id: orderId,
        order_number: orderNumber,
        type: type,
        room: roomName,
        room_number: roomNumber,
        start_time: startTime,
        duration: duration,
        isGrooming,
        isBoarding,
        isVet,
        ref_id: item.ref_id || item._id || serviceInfo?.ref_id || null,
      };
    } catch (error) {
      console.error("Error extracting booking:", error);
      return null;
    }
  };

  // Process data on mount
  useEffect(() => {
    if (isOpen && clientId && petId) {
      fetchClientData();
    } else {
      setAllOrders([]);
      setGroomingOrders([]);
    }
  }, [isOpen, clientId, petId]);

  useEffect(() => {
    if (!showCompleteTimeline && activeTab === "overall") {
      setActiveTab("overview");
    }
  }, [showCompleteTimeline, activeTab]);

  // Calculate stats
  const totalVisits = allOrders.length;
  const totalSpend = allOrders.reduce((sum, item) => sum + Number(item.price || 0), 0);
  // const outstanding = allOrders
  //   .filter(b => b.status?.toLowerCase() === "pending" || b.status?.toLowerCase() === "unpaid")
  //   .reduce((sum, item) => sum + Number(item.price || 0), 0);
  
  const totalPaid = allOrders.reduce((sum, item) => sum + Number(item.paid_amount || 0), 0);
  const outstanding = Math.max(totalSpend - totalPaid, 0);

  // Calculate days since last groomed
  const daysSinceLastGroom = lastGroomedData?.date 
    ? differenceInDays(new Date(), parseISO(lastGroomedData.date))
    : null;

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return "N/A";
      return format(date, "dd MMM yyyy");
    } catch {
      return "N/A";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      if (timeString.includes('T')) {
        const date = parseISO(timeString);
        return format(date, "hh:mm a");
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      confirmed: "bg-yellow-100 text-yellow-700",
      started: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700",
      pending: "bg-orange-100 text-orange-700",
      cancelled: "bg-red-100 text-red-700",
      "no show": "bg-red-100 text-red-700",
    };
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
  };

  // Render Grooming Card
  const renderGroomingCard = (booking) => {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{formatDate(booking.date)}</span>
              {booking.order_number && (
                <span className="text-xs text-gray-500">#{booking.order_number}</span>
              )}
              <span className="text-xs text-gray-400">Offline</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <FaCut className="text-pink-500 text-xs" />
              <span className="font-medium text-gray-800 text-sm">{booking.service_name}</span>
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(booking.status)}`}>
            {booking.status || "Pending"}
          </span>
        </div>
        
        <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-3">
          {booking.start_time && <span>🕐 {formatTime(booking.start_time)}</span>}
          {booking.duration && <span>⏱ {booking.duration}</span>}
          <span>✂️ {booking.employee_name}</span>
        </div>
        
        <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
          <span className="font-semibold text-gray-800">₹{booking.price}</span>
          {/* <span className="text-xs text-gray-500">Unpaid</span> */}
          <span className="text-xs text-gray-500">
            {Number(booking.paid_amount) >= Number(booking.price) ? "Paid" : "Unpaid"}
          </span>
        </div>
      </div>
    );
  };

const renderGroomingTimeline = () => {
  const recentGroomingOrders = groomingOrders.slice(0, 7);
  return (
    <div className="space-y-3">
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {recentGroomingOrders.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No grooming history</p>
        ) : (
          recentGroomingOrders.map((booking, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{formatDate(booking.date)}</span>
                    <span className="text-xs text-gray-400">Offline</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <FaCut className="text-pink-500 text-xs" />
                    <span className="font-medium text-gray-800 text-sm">{booking.service_name}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-3">
                <span>✂️ Groomed by: {booking.employee_name}</span>
              </div>
              {booking.ref_id && (
                <div className="mt-3 pt-2 border-t border-gray-100">
                  <GroomingSummaryPreview refId={booking.ref_id} />
                </div>
              )}
            </div>
            
          ))
        )}
      </div>
    </div>
  );
};

  // Overview Tab
  const renderOverview = () => {
    // Get recent grooming (last 3)
    const recentGrooming = groomingOrders.slice(0, 3);
    
    return (
      <div className="space-y-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-blue-50 p-2 rounded-lg text-center">
            <p className="text-xl font-bold text-blue-600">{totalVisits}</p>
            <p className="text-[10px] text-gray-600">Total Visits</p>
          </div>
          <div className="bg-green-50 p-2 rounded-lg text-center">
            <p className="text-xl font-bold text-green-600">₹{totalSpend.toFixed(0)}</p>
            <p className="text-[10px] text-gray-600">Total Spend</p>
          </div>
          <div className="bg-red-50 p-2 rounded-lg text-center">
            <p className="text-xl font-bold text-red-600">₹{outstanding.toFixed(0)}</p>
            <p className="text-[10px] text-gray-600">Outstanding</p>
          </div>
        </div>
      </div>
    );
  };


  // Overall Timeline Tab
const renderOverallTimeline = () => {
  return (
    <div className="space-y-3">
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {allOrders.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No records found</p>
        ) : (
          allOrders.map((booking, idx) => {
            const isGrooming = booking.type === "grooming";
            const isBoarding = booking.type === "boarding";
            const isVet = booking.type === "vet_clinic";
            
            return (
              <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{formatDate(booking.date)}</span>
                      {booking.order_number && (
                        <span className="text-xs text-gray-500">#{booking.order_number}</span>
                      )}
                      <span className="text-xs text-gray-400">Offline</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {isGrooming && <FaCut className="text-pink-500 text-xs" />}
                      {isBoarding && <FaCalendar className="text-purple-500 text-xs" />}
                      {isVet && <FaSyringe className="text-blue-500 text-xs" />}
                      <span className="font-medium text-gray-800 text-sm">{booking.service_name}</span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(booking.status)}`}>
                    {booking.status || "Pending"}
                  </span>
                </div>
                
                <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-3">
                  {isGrooming && (
                    <>
                      {booking.start_time && <span>🕐 {formatTime(booking.start_time)}</span>}
                      {booking.duration && <span>⏱ {booking.duration}</span>}
                      <span>✂️ {booking.employee_name}</span>
                    </>
                  )}
                  {isBoarding && (
                    <>
                      <span>🏠 {booking.room}</span>
                      {booking.end_date && <span>📅 Checkout: {formatDate(booking.end_date)}</span>}
                    </>
                  )}
                  {isVet && (
                    <>
                      {booking.start_time && <span>🕐 {formatTime(booking.start_time)}</span>}
                      <span>👨‍⚕️ {booking.employee_name}</span>
                    </>
                  )}
                </div>
                
                <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
                  <span className="font-semibold text-gray-800">₹{booking.price}</span>
                  <span className="text-xs text-gray-500">Unpaid</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

  if (!isOpen) return null;

  return (
     <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
       onClick={onClose}
      >
        <div className="h-full w-full lg:w-[60%] xl:w-[55%] bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Pet Details Header */}
        {/* {renderHeader()} */}
        <PetHeader
            serviceName="Grooming"
            petName={petName}
            petType={petType}
            petBreed={petBreed}
            petGender={petGender}
            petAge={petAge}
            petWeight={petWeight}
            petNeuteredStatus={petNeuteredStatus}
            petImage={petImage}
            petCoat={petCoat}
            petTags={petTags}
            petColor={petColor}
            petDiet={petDiet}
            petMealsPerDay={petMealsPerDay}
            petSize={petSize}
            petDetails={petDetails}
            medicalConditions={petMedicalConditions}
            vaccinations={petVaccinations}
            lastGroomedData={lastGroomedData}
            daysSinceLastGroom={daysSinceLastGroom}
            clientName={clientName}
            clientPhone={clientPhone}
            onClose={onClose}
            formatDate={formatDate}
            />

        {/* Row 6: Last Groomed */}
        <div className="mt-1 p-3 ml-4 border-t border-b mb-2 border-gray-200 flex items-center gap-4 text-xs flex-wrap">
        <div className="flex items-center gap-1">
            <FaCut className="text-pink-500 text-xs" />
            <span className="text-gray-600">Last Groomed:</span>
            <span className="font-medium text-gray-700">
            {lastGroomedData?.date ? formatDate(lastGroomedData.date) : "Not yet"}
            </span>
        </div>
        {/* Only show days if > 0 and lastGroomedData exists */}
        {lastGroomedData?.date && daysSinceLastGroom !== null && daysSinceLastGroom > 0 && (
            <div className="flex items-center gap-1">
            <FaClock className="text-gray-400 text-xs" />
            <span className="text-gray-600">Days since:</span>
            <span className="font-medium text-gray-700">{daysSinceLastGroom} days</span>
            </div>
        )}
        {lastGroomedData && (
            <div className="flex items-center gap-1">
            <span className="text-gray-500">Groomer:</span>
            <span className="font-medium text-gray-700">{lastGroomedData.employee_name}</span>
            </div>
        )}
        </div>
         
        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white ml-4">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 text-center text-sm font-semibold transition-colors relative flex items-center justify-center gap-1.5 ${
                    isActive
                      ? "text-blue-600 bg-blue-50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-blue-600" : "text-gray-400"}`} />
                  {tab.label}
                  {isActive && (
                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          {loading ? (
            <LoaderSpiner text="Loading..." />
          ) : (
            <>
              {activeTab === "grooming" && renderGroomingTimeline()}
              {activeTab === "overview" && renderOverview()}
              {activeTab === "overall" && renderOverallTimeline()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

GroomingPetDetailsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  petData: PropTypes.object,
  clientData: PropTypes.object,
  clientAppointments: PropTypes.array,
  clientBoarding: PropTypes.array,
  onRefresh: PropTypes.func,
  showCompleteTimeline: PropTypes.bool,
};

export default GroomingPetDetailsDrawer;